import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, Upload, Loader2, Palette, LayoutTemplate,
  Building2, Download, Sparkles, CreditCard, Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const TYPES = [
  { id: "business", label: "Business / Company", desc: "Showcase services & get leads" },
  { id: "portfolio", label: "Portfolio", desc: "Show your work & profile" },
  { id: "shop", label: "Online Shop", desc: "Sell products online" },
  { id: "landing", label: "Landing Page", desc: "One page to promote & convert" },
];

const COLORS = ["#066839", "#0F172A", "#1D4ED8", "#B91C1C", "#7C3AED", "#EA580C", "#0891B2", "#DB2777"];

const buildHtml = (s) => `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${s.businessName || "My Website"}</title>
<style>
:root{--c:${s.primaryColor}}
*{margin:0;box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif}
body{color:#0f172a;line-height:1.6}
.hero{background:var(--c);color:#fff;padding:96px 24px;text-align:center}
.hero img{height:72px;margin-bottom:24px;object-fit:contain}
.hero h1{font-size:44px;font-weight:800;letter-spacing:-1px}
.hero p{opacity:.9;margin-top:14px;font-size:18px;max-width:640px;margin-left:auto;margin-right:auto}
.btn{display:inline-block;margin-top:28px;background:#fff;color:var(--c);padding:14px 30px;border-radius:999px;font-weight:700;text-decoration:none}
.wrap{max-width:1000px;margin:0 auto;padding:72px 24px}
h2{color:var(--c);font-size:30px;font-weight:800;margin-bottom:18px}
.grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:20px;margin-top:24px}
.card{border:1px solid #e5e7eb;border-radius:16px;padding:24px}
.card h3{color:var(--c);margin-bottom:8px}
.contact{background:#f8fafc;text-align:center}
footer{background:var(--c);color:#fff;text-align:center;padding:28px;opacity:.95}
</style></head><body>
<section class="hero">
  ${s.logo ? `<img src="${s.logo}" alt="logo"/>` : ""}
  <h1>${s.businessName || "Your Business Name"}</h1>
  <p>${s.tagline || "Your tagline goes here."}</p>
  ${s.email ? `<a class="btn" href="mailto:${s.email}">Contact Us</a>` : ""}
</section>
<section class="wrap"><h2>About Us</h2><p>${s.about || "Tell your visitors about your business here."}</p></section>
${(s.services && s.services.length) ? `<section class="wrap" style="padding-top:0"><h2>What We Offer</h2><div class="grid">${s.services.map((x) => `<div class="card"><h3>${x}</h3></div>`).join("")}</div></section>` : ""}
<section class="wrap contact"><h2>Get In Touch</h2>
  ${s.email ? `<p>Email: ${s.email}</p>` : ""}${s.phone ? `<p>Phone: ${s.phone}</p>` : ""}${s.address ? `<p>${s.address}</p>` : ""}
</section>
<footer>© ${new Date().getFullYear()} ${s.businessName || "Your Business"} · Built with Jero-One Technologies</footer>
</body></html>`;

export default function Build() {
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const [step, setStep] = useState(0);
  const [tiers, setTiers] = useState([]);
  const [rate, setRate] = useState(3800);
  const [paying, setPaying] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [spec, setSpec] = useState({
    tier: "starter", type: "business", primaryColor: "#066839", logo: "", logoUrl: "",
    businessName: "", tagline: "", about: "", servicesText: "", email: "", phone: "", address: "",
  });

  useEffect(() => {
    api.get("/build/tiers").then(({ data }) => {
      setTiers(data.tiers);
      setRate(data.usd_to_ugx || 3800);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setSpec((p) => ({ ...p, [k]: v }));
  const services = useMemo(
    () => spec.servicesText.split("\n").map((x) => x.trim()).filter(Boolean),
    [spec.servicesText]
  );
  const previewSpec = { ...spec, services };
  const selectedTier = tiers.find((t) => t.id === spec.tier);
  const priceLabel = (usd) =>
    currency === "UGX" ? `USh ${Math.round(usd * rate).toLocaleString()}` : `$${usd}`;

  const compressImage = (file, maxSize = 512) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          let { width, height } = img;
          if (width > height && width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
          else if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
          const canvas = document.createElement("canvas");
          canvas.width = width; canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/png");
          canvas.toBlob((blob) => resolve({ dataUrl, blob }), "image/png", 0.9);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const { dataUrl, blob } = await compressImage(file);
      set("logo", dataUrl); // self-contained preview + download
      const fd = new FormData();
      fd.append("file", blob, "logo.png");
      const { data } = await api.post("/build/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logoUrl", `${API}/files/${data.path}`);
    } catch {
      toast.error("Could not upload logo. Please try a smaller image.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const download = () => {
    const blob = new Blob([buildHtml(previewSpec)], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(spec.businessName || "my-website").toLowerCase().replace(/\s+/g, "-")}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Website downloaded!");
  };

  const pay = async () => {
    if (!spec.email) { toast.error("Please enter your email so we can reach you"); return; }
    setPaying(true);
    try {
      const { logo, servicesText, ...lightSpec } = spec;
      const payload = {
        tier: spec.tier, provider,
        spec: { ...lightSpec, services, logoUploaded: !!logo },
        email: spec.email, origin_url: window.location.origin, currency,
      };
      const { data } = await api.post("/build/checkout", payload);
      if (provider === "pesapal") {
        if (data.ok && data.redirect_url) window.location.href = data.redirect_url;
        else toast.error(data.error ? `Mobile Money unavailable: ${data.error}. Please pay by Card instead.` : "Could not start Mobile Money payment. Please pay by Card instead.");
      } else {
        if (data.url) window.location.href = data.url;
        else toast.error("Could not start payment");
      }
    } catch {
      toast.error("Payment could not be started. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const steps = ["Plan", "Type", "Brand", "Content", "Preview & Pay"];
  const canNext = () => {
    if (step === 3) return spec.businessName.trim().length > 0;
    return true;
  };

  return (
    <div className="pt-16 lg:pt-20 bg-brand-50/30 min-h-screen" data-testid="build-wizard">
      <Seo
        title="Build Your Own Website | DIY Website Builder — Jero-One Technologies"
        description="Create your own professional website in minutes. Choose a plan, pick your brand colours, add your content, preview instantly and pay securely. Website design for businesses in Uganda & East Africa."
        keywords="build a website Uganda, DIY website builder Uganda, create website Kampala, website design Uganda, cheap website Uganda"
        path="/build"
      />
      <section className="bg-brand text-white noise">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
          <p className="overline text-brand-300 mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4" /> DIY Website Builder</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Build your own website in minutes.</h1>
          <p className="mt-3 text-white/70 max-w-2xl">Pick a plan, choose your look, add your content, preview it live and pay securely. Download it instantly or let our team build the full version for you.</p>
        </div>
      </section>

      {/* Stepper */}
      <div className="max-w-5xl mx-auto px-5 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl border border-brand/10 shadow-sm p-2 flex overflow-x-auto">
          {steps.map((s, i) => (
            <button
              key={s}
              data-testid={`step-${i}`}
              onClick={() => i <= step && setStep(i)}
              className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                i === step ? "bg-brand text-white" : i < step ? "text-brand" : "text-muted-foreground"
              }`}
            >
              <span className={`h-5 w-5 rounded-full flex items-center justify-center text-xs ${i <= step ? "bg-brand-500 text-white" : "bg-brand-50 text-brand"}`}>
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-brand/10 p-6 lg:p-8">

          {/* Step 0: Plan */}
          {step === 0 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand mb-1">Choose your plan</h2>
              <p className="text-muted-foreground mb-6">A one-time fee. Preview & download included; full build by our team optional.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {tiers.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`tier-${t.id}`}
                    onClick={() => set("tier", t.id)}
                    className={`text-left rounded-2xl p-5 border-2 transition-colors ${spec.tier === t.id ? "border-brand-500 bg-brand-50/50" : "border-brand/10 hover:border-brand/30"}`}
                  >
                    <p className="font-heading font-semibold text-brand">{t.name}</p>
                    <p className="font-heading font-bold text-3xl text-brand mt-3">{priceLabel(t.price)}</p>
                    <p className="text-xs text-muted-foreground mt-1">one-time</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Type */}
          {step === 1 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand mb-1 flex items-center gap-2"><LayoutTemplate className="h-6 w-6" /> What kind of website?</h2>
              <p className="text-muted-foreground mb-6">Pick the type that best fits your goal.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`type-${t.id}`}
                    onClick={() => set("type", t.id)}
                    className={`text-left rounded-2xl p-5 border-2 transition-colors ${spec.type === t.id ? "border-brand-500 bg-brand-50/50" : "border-brand/10 hover:border-brand/30"}`}
                  >
                    <p className="font-heading font-semibold text-brand">{t.label}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Brand */}
          {step === 2 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand mb-1 flex items-center gap-2"><Palette className="h-6 w-6" /> Your brand</h2>
              <p className="text-muted-foreground mb-6">Choose your main colour and upload your logo.</p>
              <Label className="mb-2 block">Primary colour</Label>
              <div className="flex flex-wrap gap-3 mb-6">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c.replace('#','')}`}
                    onClick={() => set("primaryColor", c)}
                    style={{ backgroundColor: c }}
                    className={`h-10 w-10 rounded-full transition-transform ${spec.primaryColor === c ? "ring-2 ring-offset-2 ring-brand-500 scale-110" : ""}`}
                  />
                ))}
                <input type="color" value={spec.primaryColor} onChange={(e) => set("primaryColor", e.target.value)} className="h-10 w-14 rounded-lg border border-brand/20 cursor-pointer" data-testid="color-custom" />
              </div>
              <Label className="mb-2 block">Logo (optional, under 800KB)</Label>
              <div className="flex items-center gap-4">
                <label className={`inline-flex items-center gap-2 cursor-pointer bg-brand-50 text-brand px-4 py-2.5 rounded-full text-sm font-medium hover:bg-brand-100 ${uploadingLogo ? "opacity-60 pointer-events-none" : ""}`}>
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingLogo ? "Uploading…" : "Upload logo"}
                  <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="logo-upload" />
                </label>
                {spec.logo && <img src={spec.logo} alt="logo preview" className="h-12 w-auto object-contain rounded" />}
              </div>
            </div>
          )}

          {/* Step 3: Content */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-2xl text-brand mb-1 flex items-center gap-2"><Building2 className="h-6 w-6" /> Your content</h2>
              <div><Label>Business name *</Label><Input data-testid="biz-name" value={spec.businessName} onChange={(e) => set("businessName", e.target.value)} placeholder="e.g. Acme Solutions Ltd" /></div>
              <div><Label>Tagline</Label><Input data-testid="biz-tagline" value={spec.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="A short slogan for your business" /></div>
              <div><Label>About your business</Label><Textarea data-testid="biz-about" rows={3} value={spec.about} onChange={(e) => set("about", e.target.value)} placeholder="Describe what you do..." /></div>
              <div><Label>Services / products (one per line)</Label><Textarea data-testid="biz-services" rows={4} value={spec.servicesText} onChange={(e) => set("servicesText", e.target.value)} placeholder={"Service one\nService two\nService three"} /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Email</Label><Input data-testid="biz-email" type="email" value={spec.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" /></div>
                <div><Label>Phone</Label><Input data-testid="biz-phone" value={spec.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+256 ..." /></div>
                <div><Label>Address</Label><Input data-testid="biz-address" value={spec.address} onChange={(e) => set("address", e.target.value)} placeholder="City, Country" /></div>
              </div>
            </div>
          )}

          {/* Step 4: Preview & Pay */}
          {step === 4 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand mb-1">Preview &amp; get your website</h2>
              <p className="text-muted-foreground mb-5">Here's your live preview. Download it now, or pay the {selectedTier?.name} fee to have our team build the full version.</p>
              <div className="rounded-2xl overflow-hidden border border-brand/15 shadow-sm mb-6">
                <iframe title="Website preview" srcDoc={buildHtml(previewSpec)} className="w-full h-[520px] bg-white" data-testid="site-preview" />
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button data-testid="download-site" onClick={download} variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-50 h-12 px-6">
                  <Download className="h-4 w-4 mr-2" /> Download this website (free)
                </Button>
              </div>

              <div className="bg-brand-50/60 rounded-2xl p-6">
                <p className="font-heading font-bold text-brand text-lg">Want us to build & launch the full version?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Pay the <b>{selectedTier?.name}</b> fee — <b>{selectedTier ? priceLabel(selectedTier.price) : ""}</b> — and our team will build, polish and connect your domain. We'll email you at the address below.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div><Label>Your email *</Label><Input data-testid="pay-email" type="email" value={spec.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" /></div>
                  <div>
                    <Label>Payment method</Label>
                    <div className="flex gap-2 mt-1">
                      <button data-testid="pay-stripe" onClick={() => setProvider("stripe")} className={`flex-1 rounded-full py-2.5 text-sm font-medium border flex items-center justify-center gap-2 ${provider === "stripe" ? "bg-brand text-white border-brand" : "border-brand/20 text-brand hover:bg-brand-50"}`}><CreditCard className="h-4 w-4" /> Card</button>
                      <button data-testid="pay-pesapal" onClick={() => setProvider("pesapal")} className={`flex-1 rounded-full py-2.5 text-sm font-medium border flex items-center justify-center gap-2 ${provider === "pesapal" ? "bg-brand text-white border-brand" : "border-brand/20 text-brand hover:bg-brand-50"}`}><Smartphone className="h-4 w-4" /> Mobile Money</button>
                    </div>
                  </div>
                </div>
                <Button data-testid="build-pay-btn" onClick={pay} disabled={paying} className="mt-5 rounded-full bg-brand hover:bg-brand-700 text-white h-12 px-8">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay {selectedTier ? priceLabel(selectedTier.price) : ""} & request full build <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex justify-between mt-8 pt-6 border-t border-brand/10">
            <Button
              data-testid="wizard-back"
              variant="ghost"
              onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
              className="rounded-full text-brand hover:bg-brand-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> {step === 0 ? "Exit" : "Back"}
            </Button>
            {step < 4 && (
              <Button
                data-testid="wizard-next"
                onClick={() => canNext() ? setStep(step + 1) : toast.error("Please enter your business name")}
                className="rounded-full bg-brand hover:bg-brand-700 text-white px-7"
              >
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
